import { useState } from 'react'
import { clsx } from 'clsx'
import type { ConsiliumActionPlan } from '../../stores/chatStore'

const therapistBorder: Record<string, string> = {
  wendy: 'border-wendy',
  artem: 'border-artem',
  finch: 'border-finch',
  una: 'border-una',
}

const therapistText: Record<string, string> = {
  wendy: 'text-wendy',
  artem: 'text-artem',
  finch: 'text-finch',
  una: 'text-una',
}

const speakerNames: Record<string, string> = {
  wendy: 'Венди',
  artem: 'Артём',
  finch: 'Гарольд',
  una: 'Уна',
}

interface ConsiliumActionPlanCardProps {
  plan: ConsiliumActionPlan
}

function formatPlanAsText(plan: ConsiliumActionPlan): string {
  const lines: string[] = ['ПЛАН ДЕЙСТВИЙ — Консилиум', '', plan.synthesis, '']
  if (plan.keyInsights.length > 0) {
    lines.push('КЛЮЧЕВЫЕ НАБЛЮДЕНИЯ:')
    plan.keyInsights.forEach(i => lines.push(`  • ${i}`))
    lines.push('')
  }
  if (plan.goals.length > 0) {
    lines.push('ЦЕЛИ:')
    plan.goals.forEach(g => {
      const therapistInfo = g.therapist ? ` (${speakerNames[g.therapist] || g.therapist})` : ''
      lines.push(`  • ${g.description}${therapistInfo} — ${g.timeline}`)
    })
    lines.push('')
  }
  if (plan.actionSteps.length > 0) {
    lines.push('КОНКРЕТНЫЕ ШАГИ:')
    plan.actionSteps.forEach((s, i) => lines.push(`  ${i + 1}. ${s.step} — ${s.why} (${s.timeline})`))
    lines.push('')
  }
  if (plan.focusAreas.length > 0) {
    lines.push(`ФОКУС: ${plan.focusAreas.join(', ')}`)
  }
  return lines.join('\n')
}

export default function ConsiliumActionPlanCard({ plan }: ConsiliumActionPlanCardProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formatPlanAsText(plan))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mx-4 mb-4 rounded-2xl overflow-hidden shadow-soft border border-neutral-200 bg-white">
      {/* Header */}
      <div className="px-4 py-3 bg-finch text-white flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <span className="font-medium">План действий</span>
      </div>

      <div className="p-4 space-y-4">
        {/* Synthesis */}
        <p className="text-neutral-700">{plan.synthesis}</p>

        {/* Key Insights */}
        {plan.keyInsights.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-2">Ключевые наблюдения</h4>
            <ul className="space-y-1.5">
              {plan.keyInsights.map((insight, i) => (
                <li key={i} className="flex gap-2 text-sm text-neutral-700">
                  <span className="text-primary-500 mt-0.5">&#8226;</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Goals */}
        {plan.goals.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-2">Цели</h4>
            <div className="space-y-2">
              {plan.goals.map((goal, i) => (
                <div
                  key={i}
                  className={clsx(
                    'rounded-xl p-3 border-l-4',
                    goal.therapist ? (therapistBorder[goal.therapist] || 'border-primary-500') : 'border-primary-500',
                    'bg-neutral-50'
                  )}
                >
                  <p className="text-sm text-neutral-800">{goal.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {goal.therapist && (
                      <span className={clsx('text-xs font-medium', therapistText[goal.therapist] || 'text-primary-500')}>
                        {speakerNames[goal.therapist] || goal.therapist}
                      </span>
                    )}
                    <span className="text-xs text-neutral-400">{goal.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Steps */}
        {plan.actionSteps.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-2">Конкретные шаги</h4>
            <div className="space-y-2">
              {plan.actionSteps.map((step, i) => (
                <div key={i} className={clsx(
                  'rounded-xl p-3 bg-neutral-50 border-l-4',
                  step.priority === 'high' ? 'border-red-400' : step.priority === 'medium' ? 'border-yellow-400' : 'border-neutral-300'
                )}>
                  <p className="text-sm font-medium text-neutral-800">{step.step}</p>
                  <p className="text-xs text-neutral-500 mt-1">{step.why}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-neutral-400">{step.timeline}</span>
                    <span className={clsx(
                      'text-xs px-1.5 py-0.5 rounded-full',
                      step.priority === 'high' ? 'bg-red-50 text-red-600' : step.priority === 'medium' ? 'bg-yellow-50 text-yellow-700' : 'bg-neutral-100 text-neutral-500'
                    )}>
                      {step.priority === 'high' ? 'Важно' : step.priority === 'medium' ? 'Средний' : 'Обычный'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Focus Areas */}
        {plan.focusAreas.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {plan.focusAreas.map((area, i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                {area}
              </span>
            ))}
          </div>
        )}

        {/* Next Consilium */}
        {plan.nextConsilium && (
          <div className="rounded-xl p-3 bg-finch/5 border border-finch/20">
            <p className="text-xs text-neutral-500">Следующий консилиум: <span className="font-medium text-neutral-700">{plan.nextConsilium.recommendedIn}</span></p>
            {plan.nextConsilium.triggers.length > 0 && (
              <p className="text-xs text-neutral-400 mt-1">Триггеры: {plan.nextConsilium.triggers.join(', ')}</p>
            )}
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleCopy}
          className="w-full py-2.5 rounded-xl border border-finch/30 text-finch font-medium text-sm hover:bg-finch/5 transition-colors flex items-center justify-center gap-2"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Скопировано!
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Сохранить план
            </>
          )}
        </button>
      </div>
    </div>
  )
}
